<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import { required, decimal } from 'vuelidate/lib/validators'
import DualList from 'src/components/Shared/DualList'
import slGrid from 'src/components/Shared/SLGrid'
import OrderTypeModal from 'src/components/Order/OrderTypeModal'
import OrderUserOverrides from 'src/components/Order/OrderUserOverrides'
import OrderCourseOverrides from 'src/components/Order/OrderCourseOverrides'
import OrderAffiliateOverrides from 'src/components/Order/OrderAffiliateOverrides'
import UserAutoComplete from 'src/components/User/UserAutoComplete'

export default {
  name: 'OrderManagementModal',
  components: { OrderTypeModal, OrderUserOverrides, OrderCourseOverrides, OrderAffiliateOverrides, UserAutoComplete },
  async mounted () {
    await this.fetchOrderTypes()
    this.selectOrderType(this.orderTypeOptions[0].value)
    this.fetchOrderTypeOverrides()
  },
  validations () {
    return {
      orderTypeForm: {
        SHIPPING_METHODS: {
          $each: {
            PRICE: { required },
            LK_SHIPPING_TYPE_ID: { required }
          }
        }
      }
    }
  },
  data () {
    return {
      module: this.$route.meta.module,
      loading: false,
      addOrderType: false,
      addUserOverride: false,
      addCourseOverride: false,
      addAffiliateOverride: false,
      maxResults: 5,
      editable: false,
      maximized: false,
      userFilter: null,
      courseFilter: null,
      affiliateFilter: null,
      headers: { 'Content-Type': 'text/rtf' },
      selectable: {
        type: 'multiple',
        label: 'Override',
        selected: []
      },
      columns: [
        { name: 'desc', required: true, label: 'Dessert', field: 'name', sortable: true, type: String, align: "left"},
        { name: 'calories', label: 'Calories', field: 'calories', sortable: true, type: Number, editable: true, },
        { name: 'fat', label: 'Fat (g)', field: 'fat', sortable: true, type: Number, editable: true, },
        { name: 'carbs', label: 'Carbs (g)', field: 'carbs', type: Number },
        { name: 'protein', label: 'Protein (g)', field: 'protein', type: Number },
        { name: 'sodium', label: 'Sodium (mg)', field: 'sodium', type: Number },
        { name: 'calcium', label: 'Calcium (%)', field: 'calcium', type: Number, sortable: true, sort: (a, b) => parseInt(a, 10) - parseInt(b, 10) },
        { name: 'iron', label: 'Iron (%)', field: 'iron', type: Number, sortable: true, sort: (a, b) => parseInt(a, 10) - parseInt(b, 10) }
      ],
    }
  },
  computed: {
    ...mapState('orders', {
      orderManagementModal: (state) => state.orderManagementModal,
      orderForm: state => state.orderForm,
      orderTypeForm: state => state.orderTypeForm,
      orderTypes: state => state.orderTypes,
      selectedUserOverrides: state => state.selectedUserOverrides,
      selectedCourseOverrides: state => state.selectedCourseOverrides,
      selectedAffiliateOverrides: state => state.selectedAffiliateOverrides
    }),
    ...mapState({
      detailsModal: (state) => state.shared.detailsModal,
    }),
    ...mapGetters('orders', [
      'orderTypeOptions',
      'userOverrideOptions',
      'courseOverrideOptions',
      'affiliateOverrideOptions'
    ]),
    ...mapGetters([
      'shippingTypeOptions'
    ]),
    pagination () {
      return {
        sortBy: null,
        descending: false,
        rowsNumber: this.data.length,
        rowsPerPage: 5,
        page: 1
      }
    },
    fileTypeInfo () {
      return `Only accepts ${this.fileExtensions} extensions`
    }
  },
  methods: {
    ...mapMutations('orders', [
      'setOrderTypeForm',
      'updateOrderForm',
      'updateOrderTypeForm',
      'addShippingMethod',
      'removeShippingMethod',
      'openOrderManagementModal',
      'closeOrderManagementModal',
      'updateSelectedTab',
      'openOrderType',
      'clearOrderTypeForm',
      'clearSelectedUserOverrides',
      'clearSelectedCourseOverrides',
      'clearSelectedAffiliateOverrides'
    ]),
    ...mapActions('orders', [
      'fetchOrderTypes',
      'fetchOrderTypeOverrides',
      'createOrderType',
      'updateOrderType',
      'removeOrderType',
      'updateUserOverrides',
      'updateCourseOverrides',
      'updateAffiliateOverrides',
      'createUserOverride',
      'createCourseOverride',
      'createAffiliateOverride',
      'removeUserOverrides',
      'removeCourseOverrides',
      'removeAffiliateOverrides',
    ]),
    remainingShippingTypeOptions (shippingTypeId) {
      let remainingOptions = _.filter(this.shippingTypeOptions, shippingType => {
        let found = false
        _.each(this.orderTypeForm.SHIPPING_METHODS, shippingMethod => {
          if(shippingMethod.LK_SHIPPING_TYPE_ID === shippingType.value) {
            found = true
          }
        })
        return !found
      })
      if(shippingTypeId) {
        const selectedShippingTypeOption = _.find(this.shippingTypeOptions, shippingType => shippingType.value === shippingTypeId)
        remainingOptions.unshift(selectedShippingTypeOption)
      }
      return remainingOptions
    },
    async selectOrderType (orderTypeId) {
      await this.fetchOrderTypes()
      const selectedOrderType = _.find(this.orderTypeOptions, orderType => orderType.item.ORDER_TYPE_ID === orderTypeId).item
      this.clearOrderTypeForm()
      this.setOrderTypeForm({ selectedOrderType })
      this.fetchOrderTypeOverrides()
    },
    addShipping() {
      return this.addShippingMethod()
    },
    forceSingleDefault(shippingMethodIndex, shippingMethods) {
      shippingMethods.forEach(shippingMethod => shippingMethod.IS_DEFAULT = false)
      shippingMethods[shippingMethodIndex].IS_DEFAULT = true
    },
    onAddOrderType () {
      this.clearOrderTypeForm()
      this.addOrderType = true
    },
    onAddUserOverride () {
      this.userFilter = ''
      this.addUserOverride = true
    },
    onAddCourseOverride () {
      this.courseFilter = ''
      this.addCourseOverride = true
    },
    onAddAffiliateOverride () {
      this.affiliateFilter = ''
      this.addAffiliateOverride = true
    },
    onSubmitOrderType () {
      this.createOrderType()
      .then(response => {
        const orderTypeId = response.payload.newOrderType.ORDER_TYPE_ID
        this.addOrderType = false
        this.selectOrderType(orderTypeId)
      })
    },
    onSelectUser (user) {
      this.createUserOverride(user.item.USER_ID)
      .then(this.onClearUserOverride())
    },
    onSelectCourse (course) {
      this.createCourseOverride(course.item.COURSE_ID)
      .then(this.onClearCourseOverride())
    },
    onSelectAffiliate (affiliate) {
      this.createAffiliateOverride(affiliate.item.USER_ID)
      .then(this.onClearAffiliateOverride())
    },
    onSearchUser (s, done) {
      this.$store.dispatch('users/fetchAutoComplete', { s })
      setTimeout(() => {
        done(this.userOverrideOptions)
      }, 1000)
    },
    onSearchCourse (s, done) {
      this.$store.dispatch('courses/fetchAutoComplete', { s })
      setTimeout(() => {
        done(this.courseOverrideOptions)
      }, 1000)
    },
    onSearchAffiliate (s, done) {
      this.$store.dispatch('users/fetchAffiliateAutoComplete', { s })
      setTimeout(() => {
        done(this.affiliateOverrideOptions)
      }, 1000)
    },
    onSaveOrderType () {
      this.updateOrderType()
      .then(this.fetchOrderTypes())
    },
    onSaveCourseOverrides () {
      if(this.selectedCourseOverrides.length === 0) {
        this.$q.notify({
          message: 'You must select rows to update',
          type: 'warning'
        })
      } else {
        this.updateCourseOverrides()
      }
    },
    onSaveUserOverrides () {
      if(this.selectedUserOverrides.length === 0) {
        this.$q.notify({
          message: 'You must select rows to update',
          type: 'warning'
        })
      } else {
        this.updateUserOverrides()
      }
    },
    onSaveAffiliateOverrides () {
      if(this.selectedAffiliateOverrides.length === 0) {
        this.$q.notify({
          message: 'You must select rows to update',
          type: 'warning'
        })
      } else {
        this.updateAffiliateOverrides()
      }
    },
    onRemoveUserOverrides () {
      this.removeUserOverrides()
      .then(this.clearSelectedUserOverrides())
      .then(this.fetchOrderTypeOverrides())
    },
    onRemoveCourseOverrides () {
      this.removeCourseOverrides()
      .then(this.clearSelectedCourseOverrides())
      .then(this.fetchOrderTypeOverrides())
    },
    onRemoveAffiliateOverrides () {
      this.removeAffiliateOverrides()
      .then(this.clearSelectedAffiliateOverrides())
      .then(this.fetchOrderTypeOverrides())
    },
    onDeleteOrderType () {
      let vm = this
      vm.removeOrderType()
        .then(this.clearOrderTypeForm())
        .then(this.fetchOrderTypes())
    },
    onClearOrderType() {
      this.addOrderType = false
    },
    onClearUserOverride () {
      this.userFilter = ''
      this.addUserOverride = false
    },
    onClearCourseOverride () {
      this.courseFilter = ''
      this.addCourseOverride = false
    },
    onClearAffiliateOverride () {
      this.affiliateFilter = ''
      this.addAffiliateOverride = false
    },
    resetInputs () {

    },
    toggleMaximized() {
      this.maximized = !this.maximized
    }
  }
}
</script>

<template>
<q-modal v-model="orderManagementModal.open"
  id="orderManagement"
  class="sl-modal"
  :maximized="maximized"
  :content-css="{minWidth: '80vw', minHeight: '60vh' }"
  :no-esc-dismiss="true"
  :no-backdrop-dismiss="true"
  >
  <q-modal-layout>
    <q-toolbar slot="header">
      <q-icon name="cloud upload"></q-icon>
      <q-toolbar-title>
        Order Management
      </q-toolbar-title>
      <q-icon @click.native="toggleMaximized()" size="20px" class="gt-sm" name="filter_none" />

      <q-icon @click.native="closeOrderManagementModal()" name="clear" />

    </q-toolbar>
    <q-toolbar slot="footer">
      <q-btn v-if="orderManagementModal.selectedTab === 'users'" label="Remove" color="red" @click="onRemoveUserOverrides()" :disable="selectedUserOverrides.length <= 0"/>
      <q-btn v-if="orderManagementModal.selectedTab === 'courses'" label="Remove" color="red" @click="onRemoveCourseOverrides()" :disable="selectedCourseOverrides.length <= 0"/>
      <q-btn v-if="orderManagementModal.selectedTab === 'affiliates'" label="Remove" color="red" @click="onRemoveAffiliateOverrides()" :disable="selectedAffiliateOverrides.length <= 0"/>
      <q-toolbar-title>
      </q-toolbar-title>
      <q-btn v-if="orderManagementModal.selectedTab === 'details'" label="Save" color="green" @click="onSaveOrderType()"/>
      <q-btn v-if="orderManagementModal.selectedTab === 'users'" label="Save" color="green" @click="onSaveUserOverrides()" :disable="selectedUserOverrides.length <= 0"/>
      <q-btn v-if="orderManagementModal.selectedTab === 'courses'" label="Save" color="green" @click="onSaveCourseOverrides()" :disable="selectedCourseOverrides.length <= 0"/>
      <q-btn v-if="orderManagementModal.selectedTab === 'affiliates'" label="Save" color="green" @click="onSaveAffiliateOverrides()" :disable="selectedAffiliateOverrides.length <= 0"/>
      <q-btn style="margin-left:6px" color="secondary" outline @click.native="closeOrderManagementModal()" label="Close" />
    </q-toolbar>
    <div id="orderManagementBody" class="layout-padding">
      <div class="sl-row sl-abs">
        <div class="sl-col-3 navigation" style="padding: 30px 24px;">
          <div class="row">
            <q-select v-show="!addOrderType" float-label="Order Type" :options="orderTypeOptions" :value="orderTypeForm.ORDER_TYPE_ID" @change="val => selectOrderType(val)" class="col-8"/>
            <q-input v-show="addOrderType" float-label="Enter Order Type" class="col-8" :value="orderTypeForm.ORDER_TYPE_NAME" @input="val => updateOrderTypeForm({ field: 'ORDER_TYPE_NAME', value: val })"/>
            <!-- <q-btn id="addOrderType" icon="add" @click="addOrderType = true"/> -->
            <q-btn v-show="!addOrderType" id="addOrderType" flat icon="add" class="col-2 q-ml-md" @click="onAddOrderType()">
              <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Add an Order Type</q-tooltip>
            </q-btn>
            <q-btn v-show="addOrderType" id="submitOrderType" flat color="green" icon="check" class="col-2" @click="onSubmitOrderType()" @>
              <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Submit New Order Type</q-tooltip>
            </q-btn>
            <q-btn v-show="addOrderType" id="cancelOrderType" flat color="red" icon="clear" class="col-2" @click="onClearOrderType()">
              <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Cancel New Order Type</q-tooltip>
            </q-btn>
          </div>

          <q-list highlight v-if="orderTypeForm.ORDER_TYPE_ID">
            <q-item :class="{active: orderManagementModal.selectedTab === 'details'}" @click.native="updateSelectedTab('details')" >
              <q-item-main label="Overview" />
            </q-item>
            <q-item-separator />
            <div v-if="orderTypeForm.ORDER_TYPE_ID">
              <q-list-header>Overrides</q-list-header>
              <q-item :class="{active: orderManagementModal.selectedTab === 'courses'}" @click.native="updateSelectedTab('courses')">
                <q-item-main v-show="!addCourseOverride" label="Courses" />
                <q-item-main v-show="addCourseOverride">
                  <q-field error-label="Invalid Date">
                    <q-search v-model="courseFilter" >
                      <q-autocomplete 
                        ref="courseAutoComplete"
                        float-label="Search Users"
                        :debounce="500"
                        :max-results="maxResults"
                        @search="onSearchCourse"
                        @selected="onSelectCourse"
                      />
                    </q-search>
                  </q-field>
                </q-item-main>
                <q-item-side>
                  <q-item-tile>
                    <q-btn v-show="!addCourseOverride" class="addOverrideBtn" icon="add" @click="onAddCourseOverride()">
                      <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Add a Course Override</q-tooltip>
                    </q-btn>
                    <q-btn v-show="addCourseOverride" class="clearOverrideBtn" flat color="red" icon="clear" @click="onClearCourseOverride()">
                      <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Clear</q-tooltip>
                    </q-btn>
                  </q-item-tile>
                </q-item-side>
              </q-item>
              <q-item :class="{active: orderManagementModal.selectedTab === 'users'}" @click.native="updateSelectedTab('users')">
                <q-item-main v-show="!addUserOverride" label="Users" />
                <q-item-main v-show="addUserOverride"> 
                    <q-search v-model="userFilter" float-label="Search Users">
                      <q-autocomplete 
                        ref="userAutoComplete"
                        :debounce="500"
                        :max-results="maxResults"
                        @search="onSearchUser"
                        @selected="onSelectUser"
                      />
                    </q-search>
                </q-item-main>

                <q-item-side>
                  <q-item-tile>
                    <q-btn v-show="!addUserOverride" class="addOverrideBtn" icon="add" @click="onAddUserOverride()">
                      <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Add a User Override</q-tooltip>
                    </q-btn>
                    <!-- <q-btn v-show="addUserOverride" class="submitOverrideBtn" color="green" icon="check" @click="addUserOverride = false">
                      <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Submit</q-tooltip>
                    </q-btn> -->
                    <q-btn v-show="addUserOverride" class="clearOverrideBtn" flat color="red" icon="clear" @click="onClearUserOverride()">
                      <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Clear</q-tooltip>
                    </q-btn>
                  </q-item-tile>
                </q-item-side>
              </q-item>
              <q-item :class="{active: orderManagementModal.selectedTab === 'affiliates'}" @click.native="updateSelectedTab('affiliates')">
                <q-item-main v-show="!addAffiliateOverride" label="Affiliates" />
                <q-item-main v-show="addAffiliateOverride"> 
                    <q-search v-model="affiliateFilter" float-label="Search Affiliates">
                      <q-autocomplete 
                        ref="affiliateAutoComplete"
                        :debounce="500"
                        :max-results="maxResults"
                        @search="onSearchAffiliate"
                        @selected="onSelectAffiliate"
                      />
                    </q-search>
                </q-item-main>

                <q-item-side>
                  <q-item-tile>
                    <q-btn v-show="!addAffiliateOverride" class="addOverrideBtn" icon="add" @click="onAddAffiliateOverride()">
                      <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Add a Affiliate Override</q-tooltip>
                    </q-btn>
                    <q-btn v-show="addAffiliateOverride" class="clearOverrideBtn" flat color="red" icon="clear" @click="onClearAffiliateOverride()">
                      <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Clear</q-tooltip>
                    </q-btn>
                  </q-item-tile>
                </q-item-side>
              </q-item>
            </div>
            <!-- <q-item :class="{active:selectedTab === 'students'}" @click.native="updateSelectedTab('students')">
              <q-item-main label="Students" />
              <q-item-side left>
                  <div class="label round small ">{{this.currentSessionOrFirst('Students').students.length}}</div>
              </q-item-side>
            </q-item>
            <q-item :class="{active:selectedTab === 'documents'}" @click.native="updateSelectedTab('documents')">
              <q-item-main label="Documents" />
              <q-item-side right>
                  <div class="label round small ">3</div>
              </q-item-side>
            </q-item>
            <q-item :class="{active:selectedTab === 'certificates'}" @click.native="updateSelectedTab('certificates')">
              <q-item-main label="Certificates" />
            </q-item>
            <q-item id="class-code">
              <q-field icon="memory">
                <q-input
                  :value="sessionInformation.code"
                  color="bg-grey-5"
                  float-label="Class Code"
                  :disable="true"
                />
              </q-field>
            </q-item> -->
          </q-list>
        </div>
        <div id="class-modal-right" class="sl-col-9 sl-vp no-padding full-height" style="padding-bottom:60px">
          <!-- Details -->
          <div v-if="orderManagementModal.selectedTab === 'details'" class="full-height">
            <div v-if="orderTypeForm.ORDER_TYPE_ID === undefined" class="absolute-center">
              <span class="label bg-primary">Please Select an Order Type!</span>
            </div> 
            <div v-else class="q-pa-lg fit">
              <div class="row"> 
                <h5>Defaults</h5>
              </div>
              <div class="q-pb-sm">
                <div class="row">
                  <q-field class="q-pa-md col-4">
                    <q-input float-label="Name" :value="orderTypeForm.ORDER_TYPE_NAME" @input="val => updateOrderTypeForm({ field: 'ORDER_TYPE_NAME', value: val })"/>
                  </q-field>
                  <q-field class="q-pa-md col-4">
                    <q-input stack-label="Price" type="number" prefix="$" :value="orderTypeForm.PRICE" @input="val => updateOrderTypeForm({ field: 'PRICE', value: val })"/>
                  </q-field>
                </div>
                <div class="row">
                  <q-field class="q-pa-md col-4">
                    <q-input stack-label="Minimum Amount" type="number" prefix="x" :min="0" :value="orderTypeForm.MIN_AMOUNT" @input="val => updateOrderTypeForm({ field: 'MIN_AMOUNT', value: val })"/>
                  </q-field>
                  <q-field class="q-pa-md col-4">
                    <q-input stack-label="Maximum Amount" type="number" prefix="x" :min="0" :value="orderTypeForm.MAX_AMOUNT" @input="val => updateOrderTypeForm({ field: 'MAX_AMOUNT', value: val })"/>
                  </q-field>
                </div>
              </div>
              <div class="row"> 
                <h5> Shipping Methods
                  <q-btn 
                    icon="add" id="addShippingType"
                    @click="addShipping()"
                    v-show="orderTypeForm.SHIPPING_METHODS.length < shippingTypeOptions.length"
                  >
                    <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">Add a Shipping Type</q-tooltip>
                  </q-btn>
                  <div v-show="orderTypeForm.SHIPPING_METHODS.length === 0">
                    <span class="label bg-primary">Please Add a Shipping Type!</span>
                  </div> 
                </h5>
              </div>
              <div class="row" v-for="(shippingMethod, index) in orderTypeForm.SHIPPING_METHODS" :key="index">
                <q-field 
                  class="q-pa-md col-4"
                  :error="$v.orderTypeForm.SHIPPING_METHODS.$each[index].LK_SHIPPING_TYPE_ID.$error">
                  <q-select float-label="Shipping Type" v-model="shippingMethod.LK_SHIPPING_TYPE_ID" 
                    :options="remainingShippingTypeOptions(shippingMethod.LK_SHIPPING_TYPE_ID)"
                    @blur="$v.orderTypeForm.SHIPPING_METHODS.$each[index].LK_SHIPPING_TYPE_ID.$touch"
                  />
                </q-field>
                <q-field 
                  class="q-pa-md col-2"
                  :error="$v.orderTypeForm.SHIPPING_METHODS.$each[index].PRICE.$error">
                  <q-input type="number" stack-label="Price" prefix="$" 
                    v-model="shippingMethod.PRICE" 
                    @blur="$v.orderTypeForm.SHIPPING_METHODS.$each[index].PRICE.$touch"
                  />       
                </q-field>
                <q-field 
                  class="q-pa-md col-2">
                  <q-input type="number" stack-label="Item Limit" prefix="x"
                    v-model="shippingMethod.ITEM_LIMIT" 
                  />       
                </q-field>
                <q-field 
                class="q-ma-lg col-2">
                <q-toggle
                  v-model="shippingMethod.IS_DEFAULT"
                  @input="forceSingleDefault(index, orderTypeForm.SHIPPING_METHODS)"
                  label="Default"
                />
              </q-field>
                <q-btn size="md" class="float-right" icon="delete" @click="removeShippingMethod(index)">
                </q-btn>
              </div>
              <q-btn style="margin-top:60px" color="red" outline icon="delete" label="Delete Order Type" @click.native="onDeleteOrderType()" :disabled="!!orderTypeForm.ORDER_TYPE_ID || orderTypeForm.DELETABLE_IND === 0" v-if="orderTypeForm && orderTypeForm.DELETABLE_IND" />
            </div>
          </div>
          <div id="usersContainer" v-if="orderManagementModal.selectedTab === 'users'">
            <order-user-overrides />
          </div>
          <div id="coursesContainer" v-if="orderManagementModal.selectedTab === 'courses'">
            <order-course-overrides />
          </div>
          <div v-if="orderManagementModal.selectedTab === 'affiliates'">
            <order-affiliate-overrides />
          </div>
        </div>
      </div>
    </div>
  </q-modal-layout>
</q-modal>
</template>


<style lang="stylus">
@import '~variables'
#orderManagement
  .q-layout-header
    .q-btn-dropdown
        padding 0 7px
        .q-icon
            color toolbar-icon-color
            &.on-left
                margin-right 0
                font-size 27px
        .q-btn-dropdown-arrow
            display none
  .clearOverrideBtn {
  width: 5px
  }
  .submitOverrideBtn {
    width: 10px
  }
  .addOverrideBtn {
    width: 10px
  }
  .sl-modal
    &.maximized
        .modal-content
            width 100% !important
        .q-input,
        .q-datetime-input,
        h5
            transition(all .5s ease-in-out)
            opacity 1
    .approval-history
        font-size 19px
    #class-modal-right
        position relative
    #sessionDropdown
        .ellipsis
            overflow visible
    .total-students
        color text-color
        display inline-block
        padding-left 0
        padding-bottom 13px
        padding-top 20px
    .q-inner-loading
        z-index 9
    .loading
        .q-input,
        .q-datetime-input
            margin-bottom 100px
            opacity 0
        h5
            margin-top 40px
            margin-bottom 60px
            opacity 0
    h5
        margin-top 30px
        input[type="checkbox"]
            margin-left 20px
        span 
            font-size 13px
            vertical-align middle
      .navigation   
        border-right 1px solid border-color
        position relative
        .q-list
            border 0
            margin-top 20px
            margin-left -20px
            margin-right -24px
            .label
                transition(all 0.4s ease-in-out)
                &.round
                    float right
                    background #dedede
                    color #000
        .q-item-label
            font-weight 400
            font-size 18px
        .q-item 
            border-right-width 5px
            border-right-style solid
            border-right-color transparent
            padding 10px 30px
            &:hover:not(.active)
                cursor pointer
            &.active
                background transparent 
                color $primary
                border-right-color $primary
                .label
                    background $primary
                    color white
    #addOrderType
      margin-left 10px
      margin-right -16px
      padding 0 7px
      .q-icon
        color toolbar-icon-color
        &.on-left
          margin-right 0
          font-size 27px
    #submitOrderType
      margin-left 10px
      margin-right -16px
      padding 0 7px
      .q-icon
        &.on-left
          margin-right 0
          font-size 27px
    #clearOrderType
      margin-left 10px
      margin-right -16px
      padding 0 7px
      .q-icon
        color toolbar-icon-color
        &.on-left
          margin-right 0
          font-size 27px
    .addOrderInput
      width 100%
    #addSession 
        float right
        padding 4px 10px
        margin-left 10px
        margin-right -16px
    .relative
        position relative
        margin-bottom 20px
        .q-icon
            display block
            position absolute
            top 0
            right 17px
            align-items baseline
            vertical-align middle
            width 32px
            height 100%
            text-align center
            padding 6px 7px
            font-size 19px
            cursor pointer
            transition(all 0.2s ease-in-out)
            color toolbar-icon-color
            &:hover
                color $negative
    #class-code
        position absolute
        bottom 0
        left 0
        right 0
        display block
        .q-if-label-above
            font-weight 600
            font-size 20px
</style>
